import React, {
    Component,
    Fragment,
} from 'react'
import classnames from 'classnames'

import { i18n } from '../../validator'
import Select from '../../front/components/shared/sn-select'

const apiDatePattern = 'YYYY-MM-DD'
const czechDatePattern = 'DD.MM.YYYY'
const yearOnlyDatePattern = 'YYYY'

@i18n()
export default class SelectDatepicker extends Component {

    state = {
        day: 1,
        month: 1,
        year: 2000,
        fullDate: null,
        error: null,
        defaultDateLoaded: false,
    }

    componentDidMount() {
        this.updateDateFromDefaultProp()
    }

    componentDidUpdate = () => {
        this.updateDateFromDefaultProp()
    }

    /**
     * If defaultDate prop exists, set component to this date
     */
    updateDateFromDefaultProp = () => {
        const { defaultDate: currentDefaultDate } = this.props
        const { defaultDateLoaded } = this.state

        if (!defaultDateLoaded && currentDefaultDate) {
            const defaultDate = moment(currentDefaultDate)

            this.setState({
                day: defaultDate.date(),
                month: defaultDate.month() + 1,
                year: defaultDate.year(),
                defaultDateLoaded: true,
            }, this.buildFullDate)
        }
    }

    /**
     * Generate array of days in month from 1 to 31
     * @returns {Array}
     */
    getDays = () => {
        const days = []
        const firstDay = 1
        const lastDay = 31

        for (let i = firstDay; i <= lastDay; i++) {
            days.push(i)
        }

        return days
    }

    /**
     * Generate array of months
     * @returns {Array}
     */
    getMonths = () => {
        const months = []
        const _months = moment().localeData().months()
        let monthNumber = 1

        _months.forEach(item => {
            months.push({ [monthNumber++]: item })
        })

        return months
    }

    /**
     * Generate array of years from 1900 to now
     * @returns {Array}
     */
    getYears = () => {
        const years = []
        const minYear = 1900
        const maxYear = moment().format(yearOnlyDatePattern)

        for (let i = maxYear; i >= minYear; i--) {
            years.push(i)
        }

        return years
    }

    /**
     * Change state param when is date changed and after that try build full date string
     * @param {string} type
     * @param {number|string} value
     */
    onDateSelected = (type, value) => {
        this.setState({
            [type]: value,
        }, this.buildFullDate)
    }

    /**
     * Try build date string from selected date
     */
    buildFullDate = () => {
        const {
            day,
            month,
            year,
        } = this.state

        const date = moment(`${year}-${month}-${day}`, apiDatePattern)

        if (this.isDateInvalid(date)) {
            this.setState({
                fullDate: null,
            }, this.callOnDateChangedHandler)
            return
        }

        this.setState({
            error: null,
            fullDate: date.format(apiDatePattern),
        }, this.callOnDateChangedHandler)
    }

    /**
     * Check if date is invalid or is ou of range between min and max date
     * @param {moment} date
     * @returns {boolean}
     */
    isDateInvalid = date => (
        !this.checkIsDateValid(date) || this.checkAfterMaxDate(date) || this.checkBeforeMinDate(date)
    )

    /**
     * Check if date is valid date, otherwise show error
     * @param {moment} date
     * @returns {boolean}
     */
    checkIsDateValid = date => {
        const { t } = this.props

        if (!date.isValid()) {
            this.setState({
                error: t('invalidDate'),
            })
            return false
        }

        return true
    }

    /**
     * Check if selected date is less than maximum date, otherwise show error
     * @param {moment} date
     * @returns {boolean}
     */
    checkAfterMaxDate = date => {
        const {
            maxDate,
            t,
        } = this.props

        if (maxDate) {
            const isAfterMaxDate = date.isAfter(maxDate)
            if (isAfterMaxDate) {
                const formattedDate = moment(maxDate).format(czechDatePattern)
                this.setState({
                    error: t('dateMustBeLessThan', { date: formattedDate }),
                })
                return true
            }
            return false
        }

        return false
    }

    /**
     * Check if selected date is greater than minimum date, otherwise show error
     * @param {moment} date
     * @returns {boolean}
     */
    checkBeforeMinDate = date => {
        const {
            minDate,
            t,
        } = this.props

        if (minDate) {
            const isBeforeMinDate = date.isBefore(minDate)
            if (isBeforeMinDate) {
                const formattedDate = moment(minDate).format(czechDatePattern)
                this.setState({
                    error: t('dateMustBeGreaterThan', { date: formattedDate }),
                })
                return true
            }
            return false
        }

        return false
    }

    /**
     * Call handler from props with full date
     */
    callOnDateChangedHandler = () => {
        const { fullDate } = this.state
        const { onDateChange } = this.props

        if (onDateChange) {
            onDateChange(fullDate)
        }
    }

    /**
     * Renders a date container element
     * @returns {*}
     */
    renderDateContainers = () => {
        const { t } = this.props
        const {
            day,
            month,
            year,
            error,
        } = this.state

        const days = this.getDays()
        const months = this.getMonths()
        const years = this.getYears()

        return (
            <Fragment>
                <Select
                    options={days}
                    label={t('day')}
                    value={day}
                    containerClassName="mr-2"
                    onChange={e => this.onDateSelected('day', e.target.value)}
                    error={error}
                    withoutArrow
                />
                <Select
                    options={months}
                    label={t('month')}
                    value={month}
                    containerClassName="mr-2"
                    onChange={e => this.onDateSelected('month', e.target.value)}
                    error={error}
                    withoutArrow
                />
                <Select
                    options={years}
                    label={t('year')}
                    value={year}
                    onChange={e => this.onDateSelected('year', e.target.value)}
                    error={error}
                    withoutArrow
                />
            </Fragment>
        )
    }

    render() {
        const { className } = this.props
        const { error } = this.state

        return (
            <div>
                <div className={classnames({
                    'd-flex flex-row': true,
                    [className]: !!className,
                })}>
                    {this.renderDateContainers()}
                </div>
                {error &&
                    <div className="text-color-negative">{error}</div>
                }
            </div>
        )
    }
}
